/* =====================================================================
   AstaLab 2.0 — CatalogRepository (round alpha.2, obiettivo 11)

   Stesso principio di AuctionRepository/BackendAdapter (round alpha.1):
   un contratto astratto, MAI implementato direttamente dalla UI o dai
   test — solo tramite ArtifactDemoCatalogRepository (locale, sempre
   nell'Artifact) o SupabaseCatalogRepository (deploy reale, RPC +
   RLS/GRANT). La business logic di matching/import/copertura vive
   INTERAMENTE nei moduli puri di frontend/src/catalog/*Engine.js,
   condivisi da entrambe le implementazioni — questo file descrive solo
   il confine di persistenza/autorizzazione, mai un algoritmo.

   Le operazioni di IMPORT (obiettivo 9) sono admin-only sul deploy
   reale: SupabaseCatalogRepository le invia sempre a una RPC che
   verifica autorizzazione lato server (mai un flag lato client). Le
   operazioni di sola lettura sono disponibili a qualunque utente
   autenticato.
   ===================================================================== */

function notImplemented(method){
  throw new Error(`CatalogRepository.${method}() non implementato da questo repository`);
}

export class CatalogRepository{
  /** @returns {Promise<Array>} solo i giocatori current=true (obiettivo 2) */
  async listCurrentPlayers(){ return notImplemented("listCurrentPlayers"); }
  /** @returns {Promise<Object|null>} */
  async getPlayer(playerId){ return notImplemented("getPlayer"); }

  /**
   * Applica un nuovo snapshot del listone (obiettivo 2, 7) — admin-only sul deploy reale.
   * @param {Array} records - righe già normalizzate (vedi importPreview.buildListoneUpdatePreview)
   * @param {string} sourceFile
   * @param {{seasonKey:string,label:string}} seasonInfo - Revisione r6 (audit, blocker 4): per un
   *   listone cloud 2.0 NON è più nullable — buildListoneUpdatePreview() (revisione r5) già rifiuta
   *   un file senza stagione riconoscibile PRIMA che questo metodo venga mai chiamato dalla UI, e
   *   SupabaseCatalogRepository fallisce esplicitamente PRIMA della RPC se seasonInfo/seasonKey
   *   manca comunque (difesa aggiuntiva contro un chiamante diverso dal percorso UI standard) — la
   *   RPC import_catalog_snapshot() stessa rifiuta ora p_season_key=NULL come terzo backstop.
   * @param {string} fileChecksum - da preview.fileChecksum. Revisione r8 (audit, blocker 4): NON è
   *   più opzionale/ignorabile — è richiesto (stringa non vuota) dal contratto pubblico condiviso
   *   (validateListoneApplyContract), esattamente come import_catalog_snapshot() lo richiede sempre
   *   lato RPC. SupabaseCatalogRepository lo usa anche per l'idempotenza lato RPC (obiettivo 5);
   *   ArtifactDemoCatalogRepository non lo persiste (la riconciliazione per fantacalcio_id è già
   *   idempotente in effetto — riapplicare lo stesso snapshot converge allo stesso stato), ma lo
   *   richiede comunque per tenere UN SOLO contratto pubblico, identico per entrambi i repository.
   * @returns {Promise<{ok:boolean, summary:Object|null, error:string|null}>} `summary` shape
   *   (round alpha.6-r1, correzione contratto real-backend dopo un crash PROD — vedi
   *   frontend/src/ui/pages/CatalogPage.js, renderNewDepartedCard/renderListoneSummary):
   *
   *   GUARANTEED_CROSS_BACKEND_FIELDS (presenti SEMPRE, da entrambi i repository, sicuri da leggere
   *   incondizionatamente):
   *     - nuovi, aggiornati, invariati, rimossi (number)
   *     - skipped (boolean) — true su un reimport idempotente (stesso kind+seasonKey+fileChecksum):
   *       su quel path i quattro conteggi sopra sono quelli dell'import ORIGINALE, MAI di questa
   *       chiamata — un chiamante deve controllare skipped PRIMA di presentare i conteggi come un
   *       import fresco (esattamente il difetto corretto in questo round).
   *
   *   OPTIONAL_BACKEND_CAPABILITIES (presenti SOLO su ArtifactDemoCatalogRepository — la RPC reale
   *   import_catalog_snapshot, 0008, non li ha mai restituiti e non è previsto che lo faccia: sono
   *   dettaglio UI, non richiesti per la business correctness, che dipende SOLO dai conteggi sopra e
   *   dallo stato realmente persistito in public.players. Un chiamante DEVE trattarli come
   *   possibilmente assenti, mai assumerne la presenza):
   *     - nuoviPlayerIds, aggiornatiPlayerIds (Array<uuid>)
   *     - removedPlayers (Array<{id,nome,squadra,fantacalcioId}>)
   *     - autoLinked (Array<{playerId,fantacalcioId,nome,linkedSeasons,missingSeasons}>)
   *     - externalReconciliation (Object) — mai letto altrove nel codebase
   *
   *   RPC-only, mai letti dalla UI (innocui da ignorare): importId, seasonKey,
   *   playerSeasonStatsBackfilled, syntheticTestRunId.
   */
  async applyListoneSnapshot(records, sourceFile, seasonInfo, fileChecksum){ return notImplemented("applyListoneSnapshot"); }

  /**
   * Importa un archivio statistiche stagionale (obiettivo 5) — admin-only sul deploy reale.
   * @returns {Promise<{ok:boolean, summary:Object|null, error:string|null}>} `summary` shape
   *   (round alpha.6-r1): GUARANTEED_CROSS_BACKEND_FIELDS — inseriti, aggiornati, duplicati,
   *   conflitti (number), skipped (boolean, stesso significato di applyListoneSnapshot sopra — la
   *   RPC import_statistics_archive, 0008, segnala un reimport idempotente con skipped:true, MAI
   *   con duplicateFile:true, mentre lo restituisce SOLO true/false sul path non-idempotente: un
   *   chiamante deve controllare `skipped`, mai il solo `duplicateFile`, per riconoscere un
   *   reimport senza modifiche). OPTIONAL_BACKEND_CAPABILITIES — label (string, solo Artifact; la
   *   RPC reale restituisce seasonKey ma mai un'etichetta leggibile precalcolata).
   */
  async applyStatisticsImport(preview){ return notImplemented("applyStatisticsImport"); }

  /** @returns {Promise<Object>} vedi statisticsEngine.computeMultiseasonCoverage */
  async getCoverage(seasonKeys){ return notImplemented("getCoverage"); }
  /** @returns {Promise<Array>} vedi statisticsEngine.computeMissingDataRows */
  async getMissingDataRows(seasonKeys, options){ return notImplemented("getMissingDataRows"); }
  /** @returns {Promise<Object|null>} */
  async getPlayerSeasonStats(playerId, seasonKey){ return notImplemented("getPlayerSeasonStats"); }
  /** @returns {Promise<string[]>} */
  async getStatisticsTargetSeasons(){ return notImplemented("getStatisticsTargetSeasons"); }

  /** @returns {Promise<Array>} record esterni attivi (externalStatistics) */
  async listExternalRecords(){ return notImplemented("listExternalRecords"); }
  /** @returns {Promise<Array>} coda non associati */
  async listUnassociatedExternalRecords(){ return notImplemented("listUnassociatedExternalRecords"); }
  /**
   * @param {string} text - contenuto CSV
   * @param {string} [filename] - nome del file, usato per l'idempotenza (obiettivo 5, revisione r2)
   * @returns {Promise<{ok:boolean, warnings:Array, error:string|null, skipped?:boolean}>} — admin-only sul deploy reale
   */
  async importExternalStatisticsCSV(text, filename){ return notImplemented("importExternalStatisticsCSV"); }
  /**
   * Round alpha.3-r1 (fondamenta enrichment provider-neutral — vedi
   * PROVIDER_ENRICHMENT_DESIGN_alpha3_r1.md). Riceve osservazioni RAW già ottenute da un
   * ProviderAdapter (frontend/src/providers/*Adapter.js) — questo metodo non conosce mai il
   * trasporto/l'autenticazione di un provider, solo il risultato già fetchato. Le mappa/valida
   * (providerEnrichmentEngine.js) e le fa fluire nella STESSA coda/riconciliazione già usata da
   * importExternalStatisticsCSV() — mai una seconda pipeline parallela.
   * @param {Array<Object>} rawObservations - forma proprietaria dell'adapter (mai già normalizzata)
   * @param {string} providerKey - identità del provider (es. "mock-fixture-v1"), mai il valore di `source`
   * @returns {Promise<{ok:boolean, warnings:Array, error:string|null, skipped?:boolean}>} — admin-only sul deploy reale
   */
  async importProviderObservations(rawObservations, providerKey){ return notImplemented("importProviderObservations"); }
  /**
   * Revisione alpha.3-r3 (audit blocker B su r2): `providerKey` (opzionale, quinto parametro — mai
   * rompe una chiamata esistente a 4 argomenti) seleziona quale vendor per una fonte vendor-scoped
   * (oggi solo "provider-authorized" — vedi isVendorScopedExternalSource() in
   * externalRecordsEngine.js): un giocatore può avere un'associazione manuale PER vendor,
   * contemporaneamente. Obbligatorio quando `source==="provider-authorized"`, vietato altrimenti.
   * @returns {Promise<{ok:boolean, error:string|null}>}
   */
  async associateExternalId(playerId, source, sourcePlayerId, sourceUrl, providerKey){ return notImplemented("associateExternalId"); }
  /** `providerKey` opzionale (vedi associateExternalId sopra) — seleziona quale associazione vendor
   *  rimuovere per una fonte vendor-scoped; ignorato per le altre fonti.
   *  @returns {Promise<{ok:boolean, error:string|null}>} */
  async removeExternalId(playerId, source, providerKey){ return notImplemented("removeExternalId"); }
  /** @returns {Promise<{ok:boolean, error:string|null}>} */
  async setActiveExternalSource(recordId){ return notImplemented("setActiveExternalSource"); }

  /**
   * Round 2.2.0. NESSUNA UI/repository "admin-only" precedente in questo codebase ha mai esposto
   * un modo lato client per sapere se l'utente corrente è admin (import listone/statistiche,
   * associazioni esterne: la UI è sempre mostrata a qualunque utente autenticato, l'unica reale
   * applicazione è sempre stata server-side, private.is_admin() dentro la RPC). Questo metodo esiste
   * SOLO come comodità per la UI (mostrare/nascondere la sezione di gestione conflitti) — non è mai
   * il confine di sicurezza reale, che resta sempre e solo private.is_admin() lato RPC
   * (resolveStatisticsConflict sotto). Su Supabase legge profiles.is_admin della propria riga (RLS
   * profiles_select_own, 0003 — nessun nuovo GRANT/migrazione necessario). Su ArtifactDemo restituisce
   * sempre true, coerente con l'assenza di un controllo admin interno in OGNI altra operazione
   * admin-only di questo repository (identico a applyListoneSnapshot/applyStatisticsImport).
   * @returns {Promise<boolean>}
   */
  async isCurrentUserAdmin(){ return notImplemented("isCurrentUserAdmin"); }

  /**
   * Round 2.2.0 (risoluzione dei conflitti statistici UFFICIALI — vedi audit
   * 2.2.0-STATS-CONFLICT-CONTRACT-AUDIT). Elenca i conflitti APERTI su public.statistics_conflicts
   * (mai external_statistics_conflicts, esplicitamente fuori scopo). Deliberatamente distinto dal
   * campo `statisticsConflicts` (sempre vuoto qui, mai correlato) restituito internamente da
   * _loadCatalogStateForCoverage()/statisticsEngine — quel campo appartiene alla simulazione
   * client-side di anteprima import (statisticsEngine.mergeStatisticsRecord), mai al registro di
   * controllo persistito che questo metodo legge davvero. La RLS esistente permette già SELECT a
   * qualunque utente autenticato (stessa policy di catalog_imports) — l'admin-only del round 2.2.0
   * riguarda la RISOLUZIONE (resolveStatisticsConflict sotto) e la UI di gestione, mai la visibilità
   * di riga qui.
   * @returns {Promise<Array<{id:string, fantacalcioId:number, seasonKey:string, field:string,
   *   existingValue:string|null, incomingValue:string|null, sourceFile:string, detectedAt:string}>>}
   *   solo conflitti con status='open', ordinati per detectedAt.
   */
  async listOfficialStatisticsConflicts(){ return notImplemented("listOfficialStatisticsConflicts"); }

  /**
   * Round 2.2.0. Risolve un conflitto statistico UFFICIALE — admin-only, applicato server-side da
   * public.resolve_statistics_conflict() (migrazione 0021) sul deploy reale, mai una mutazione
   * diretta di statistics_conflicts/player_season_stats dal frontend.
   * @param {string} conflictId
   * @param {"keep_current"|"accept_incoming"|"custom_value"|"ignore"} action - vocabolario chiuso,
   *   mai testo libero (RESOLUTION_VOCABULARY_CLOSED=YES).
   * @param {string|null} [customValue] - obbligatorio solo per action="custom_value", deve essere
   *   NULL/omesso per ogni altra azione (un valore fornito viene comunque rifiutato, mai ignorato).
   * @returns {Promise<{ok:boolean, status:"resolved"|"ignored"|null, resolvedValue:string|null, error:string|null}>}
   */
  async resolveStatisticsConflict(conflictId, action, customValue){ return notImplemented("resolveStatisticsConflict"); }
}
